import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { useAppContext } from '../../context/AppContext';
import { PageType } from '../../types';
import './UserHeader.css';
import AvatarImage from '../UI/AvatarImage';
import { translateRole } from '../../utils/roleTranslations';
import { isUnder18 } from '../../utils/ageUtils';
import { applySpaceTheme } from '../../utils/spaceTheme';
import { isQualiopiOrg } from '../../utils/qualiopiOrg';
import { ContextOrgType, resolveSwitcherContext } from '../../utils/contextUtils';

const STUDENT_ROLES = new Set(['eleve_primaire', 'collegien', 'lyceen', 'etudiant']);

interface UserHeaderProps {
  currentPage: PageType;
  onPageChange: (page: PageType) => void;
}

const UserHeader: React.FC<UserHeaderProps> = ({ currentPage, onPageChange }) => {
  const { state, setShowingPageType } = useAppContext();
  const user = state.user;
  const navigate = useNavigate();
  const location = useLocation();

  const getCurrentContext = useMemo(
    () =>
      resolveSwitcherContext({
        search: location.search,
        showingPageType: state.showingPageType,
      }),
    [location.search, state.showingPageType]
  );

  // Process organizations from available_contexts
  const organizations = useMemo(() => {
    const contexts = state.user.available_contexts;

    const orgs: Array<{
      id: number | string;
      name: string;
      type: ContextOrgType;
      role?: string;
      isAdmin: boolean;
    }> = [];

    if (contexts) {
      // Add personal dashboard if available (at the top)
      if (contexts.user_dashboard) {
        orgs.push({
          id: 'user-dashboard',
          name: 'Tableau de bord personnel',
          type: 'user',
          isAdmin: false
        });
      }

      // Add schools (only if admin or superadmin)
      if (contexts.schools) {
        contexts.schools.forEach(school => {
          if (school.role === 'superadmin' || school.role === 'admin') {
            orgs.push({
              id: school.id,
              name: school.name,
              type: 'school',
              role: school.role,
              isAdmin: true
            });
          }
        });
      }

      // Add companies (only if admin or superadmin)
      if (contexts.companies) {
        contexts.companies.forEach(company => {
          if (company.role === 'superadmin' || company.role === 'admin') {
            orgs.push({
              id: company.id,
              name: company.name,
              type: 'company',
              role: company.role,
              isAdmin: true
            });
          }
        });
      }

      const ofFromApi = contexts.formation_organizations || [];
      ofFromApi.forEach((org) => {
        orgs.push({
          id: org.id,
          name: org.name,
          type: 'formation' as const,
          role: org.role,
          isAdmin: true,
        });
      });

      // Add teacher dashboard if available
      if (contexts.teacher_dashboard) {
        orgs.push({
          id: 'teacher-dashboard',
          name: 'Tableau de bord Enseignant',
          type: 'teacher',
          isAdmin: false
        });
      }
    }

    return orgs;
  }, [state.user.available_contexts]);

  const handlePageChange = (page: PageType) => {
    onPageChange(page);
    navigate(`/${page}`);
  };

  // Handle organization switching
  const handleOrganizationSwitch = (orgId: number | string, orgType: ContextOrgType) => {
    let newPageType: 'pro' | 'edu' | 'teacher' | 'user' | 'of';

    switch (orgType) {
      case 'school':
        newPageType = 'edu';
        break;
      case 'company':
        newPageType = 'pro';
        break;
      case 'teacher':
        newPageType = 'teacher';
        break;
      case 'user':
        newPageType = 'user';
        break;
      case 'formation':
        newPageType = 'of';
        break;
      default:
        newPageType = 'user';
    }

    // Sauvegarder le contexte choisi dans localStorage
    localStorage.setItem('selectedPageType', newPageType);
    localStorage.setItem('selectedContextId', orgId.toString());
    localStorage.setItem('selectedContextType', orgType);

    setShowingPageType(newPageType);
    applySpaceTheme(newPageType, {
      formationBrand: isQualiopiOrg(state.user, newPageType),
    });

    onPageChange('dashboard');
    navigate(`/dashboard?ctx=${orgType}-${orgId}`, { replace: true });

    console.log(`Switched to ${orgType} ${orgId}, pageType: ${newPageType}`);
  };

  const handleLogout = () => {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('selectedPageType');
    localStorage.removeItem('selectedContextId');
    localStorage.removeItem('selectedContextType');
    navigate('/login');
  };


  return (
    <header className="user-header">
      <div className="user-header-left" onClick={() => {
        onPageChange('projects');
        navigate('/projects');
      }}>
        <img src="./icons_logo/Property 1=Logo Kinship user.svg" alt="Logo" className="!w-[150px] !h-[40px] user-logo" />
      </div>

      <div className="user-header-right">
        <Menu as="div" className="relative">
          {({ open }: { open: boolean }) => (
            <>
              <Menu.Button className={`user-info ${state.showingPageType === 'user' ? 'user-dashboard-menu !flex-row' : ''}`}>
                <AvatarImage
                  src={user.avatar}
                  alt={user.name}
                  className="user-avatar"
                />
                <span className="user-name">{user.name}</span>
                <img
                  src="/icons_logo/Icon=Chevron bas.svg"
                  alt="▼"
                  className={`dropdown-arrow ${open ? 'open' : ''}`}
                />
              </Menu.Button>

          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className={`dropdown-menu ${state.showingPageType === 'user' ? 'user-dashboard-dropdown' : ''}`}>
              <div className="menu-header">
                <div className='menu-header-content'>
                  <div className="menu-header-name">{user.name}</div>
                  <div className="dropdown-role" title={translateRole(user.role)}>{translateRole(user.role)}</div>
                  <div className="menu-header-email" title={user.email}>
                    <i className="fas fa-envelope email-icon"></i>
                    <span className="email-text">{user.email}</span>
                  </div>
                </div>
              </div>

              <Menu.Item>
                {({ active }: { active: boolean }) => (
                  <button
                    className={`${active ? 'active' : ''}`}
                    onClick={() => handlePageChange('projects')}
                  >
                    Mes projets
                  </button>
                )}
              </Menu.Item>

              <Menu.Item>
                {({ active }: { active: boolean }) => (
                  <button
                    className={`${active ? 'active' : ''}`}
                    onClick={() => handlePageChange('network')}
                  >
                    Mon réseau
                  </button>
                )}
              </Menu.Item>

              <Menu.Item>
                {({ active }: { active: boolean }) => (
                  <button
                    className={`${active ? 'active' : ''}`}
                    onClick={() => handlePageChange('badges')}
                  >
                    Mes preuves
                  </button>
                )}
              </Menu.Item>

              <Menu.Item>
                {({ active }: { active: boolean }) => (
                  <button
                    className={`${active ? 'active' : ''}`}
                    onClick={() => handlePageChange('events')}
                  >
                    Mes événements
                  </button>
                )}
              </Menu.Item>

              {/* Route pik temporairement désactivée
              <Menu.Item>
                {({ active }: { active: boolean }) => (
                  <button
                    className={`${active ? 'active' : ''}`}
                    onClick={() => handlePageChange('pik')}
                  >
                    Mon identité Kinship
                  </button>
                )}
              </Menu.Item>
              */}

              {STUDENT_ROLES.has(String(user?.role || '')) && (
                <Menu.Item>
                  {({ active }: { active: boolean }) => (
                    <button
                      className={`${active ? 'active' : ''}`}
                      onClick={() => handlePageChange('mes-parents')}
                    >
                      Mes parents
                    </button>
                  )}
                </Menu.Item>
              )}

              {!isUnder18(user?.birthday) && (
                <Menu.Item>
                  {({ active }: { active: boolean }) => (
                    <button
                      className={`${active ? 'active' : ''}`}
                      onClick={() => handlePageChange('mes-enfants')}
                    >
                      Mes enfants
                    </button>
                  )}
                </Menu.Item>
              )}

              <div className="dropdown-divider" />

              {organizations.length > 0 && (
                <div className="org-section">
                  <div className="org-title">Changer d'organisation</div>
                  {organizations.map((org) => {
                    const isSelected = getCurrentContext && 
                      getCurrentContext.id.toString() === org.id.toString() && 
                      getCurrentContext.type === org.type;
                    return (
                      <Menu.Item key={`${org.type}-${org.id}`}>
                        {({ active }: { active: boolean }) => (
                          <button
                            type="button"
                            className={`org-item ${active ? 'active' : ''} ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleOrganizationSwitch(org.id, org.type)}
                          >
                            <span className="org-item-name">{org.name}</span>
                            <span className="org-item-meta">
                              {isSelected && <span className="selected-indicator">●</span>}
                              {org.isAdmin && <span className="admin-tag">Admin</span>}
                            </span>
                          </button>
                        )}
                      </Menu.Item>
                    );
                  })}
                </div>
              )}

              <Menu.Item>
                {({ active }: { active: boolean }) => (
                  <button
                    type="button"
                    className={`menu-item ${active ? 'active' : ''}`}
                    onClick={() => handlePageChange('personal-settings')}
                  >
                    <i className="fas fa-cog"></i> Paramètres personnels
                  </button>
                )}
              </Menu.Item>

              <Menu.Item>
                {({ active }: { active: boolean }) => (
                  <button
                    type="button"
                    className={`menu-item logout-item ${active ? 'active' : ''}`}
                    onClick={handleLogout}
                  >
                    <i className="fas fa-sign-out-alt logout-icon"></i> Se déconnecter
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
            </>
          )}
        </Menu>
      </div>
    </header>
  );
};

export default UserHeader;
